import { useEffect, useState } from "react";
import { Routes, Route, Navigate, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LifeBuoy, Plus, ArrowLeft, Send, LogOut, Ticket, BookOpen, Search, FileText } from "lucide-react";
import { StatusBadge, SeverityBadge } from "@/components/Primitives";
import KBContent from "@/components/KBContent";
import { BRAND_TITLE, BRAND_BADGE } from "@/lib/brand";

export const CLIENT_ROLES = ["end_user", "client_administrator", "client_manager", "client_read_only"];
export const isClientRole = (role) => CLIENT_ROLES.includes(role);

const CATEGORIES = ["General", "Password / Login", "Email", "Hardware", "Software", "Network / Wi-Fi", "Access Request", "Printer", "Other"];
const PRIORITIES = ["low", "medium", "high", "critical"];

function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/portal/me").then(r => setMe(r.data)).catch(() => {}); }, []);
  const onKb = loc.pathname.startsWith("/support/kb");
  const tab = (active) => "inline-flex items-center gap-1.5 text-sm px-1 pb-1 border-b-2 " + (active ? "border-blue-500 text-zinc-100" : "border-transparent text-zinc-400 hover:text-zinc-200");
  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <header className="h-14 border-b border-zinc-900 flex items-center px-5 gap-5">
        <Link to="/support" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center font-mono font-bold text-white text-sm">{BRAND_BADGE}</div>
          <div className="leading-tight">
            <div className="font-display font-semibold text-[15px] tracking-tight">{BRAND_TITLE}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 -mt-0.5">Support</div>
          </div>
        </Link>
        <nav className="flex items-center gap-4 h-full pt-3">
          <Link to="/support" className={tab(!onKb)} data-testid="portal-tab-requests"><Ticket size={15} /> My Requests</Link>
          <Link to="/support/kb" className={tab(onKb)} data-testid="portal-tab-kb"><BookOpen size={15} /> Knowledge Base</Link>
        </nav>
        <div className="ml-auto flex items-center gap-3 text-sm">
          <span className="text-zinc-400 hidden sm:inline">{me?.name || user?.name}{me?.company ? ` · ${me.company}` : ""}</span>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="portal-logout" className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-zinc-100"><LogOut size={15} /> Sign out</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-6">{children}</main>
    </div>
  );
}

function KbList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/portal/kb").then(r => setItems(asArray(r.data))).catch(() => setItems([])).finally(() => setLoading(false)); }, []);
  const cats = Array.from(new Set(items.map(d => d.category || "General")));
  const filtered = items.filter(d => !q || `${d.title} ${(d.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Self-Help</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Knowledge Base</h1>
          <p className="text-sm text-zinc-500 mt-1">Guides for common issues — try these before raising a request.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search help articles" data-testid="kb-search" className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-64 focus:outline-none focus:border-blue-500/60" />
        </div>
      </div>
      {loading ? <div className="text-zinc-500 text-sm">Loading…</div> : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">No articles found.</div>
      ) : cats.map(cat => {
        const inCat = filtered.filter(d => (d.category || "General") === cat);
        if (inCat.length === 0) return null;
        return (
          <div key={cat}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2 mt-4">{cat}</div>
            <div className="bg-zinc-950 border border-zinc-900 rounded-md divide-y divide-zinc-900">
              {inCat.map(d => (
                <Link key={d.id} to={`/support/kb/${d.id}`} data-testid={`kb-item-${d.id}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-zinc-900/40">
                  <FileText size={16} className="text-blue-400 shrink-0" />
                  <div className="flex-1 min-w-0"><div className="text-sm text-zinc-100 truncate">{d.title}</div></div>
                  <span className="text-[10px] text-zinc-500 font-mono uppercase">{d.doc_type}</span>
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KbArticle() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/portal/kb/${id}`).then(r => setD(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="space-y-4"><Link to="/support/kb" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200"><ArrowLeft size={15} /> Back</Link><div className="text-sm text-zinc-500">Article not found.</div></div>;
  if (!d) return <div className="text-zinc-500 text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <Link to="/support/kb" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200"><ArrowLeft size={15} /> Back to Knowledge Base</Link>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-blue-400 font-mono">{d.category} · {d.doc_type}</div>
        <h1 className="font-display text-2xl font-semibold tracking-tight mt-0.5">{d.title}</h1>
      </div>
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-6"><KBContent text={d.content} /></div>
      <div className="text-center text-xs text-zinc-600 pt-2">Didn't solve it? <Link to="/support/new" className="text-blue-400 hover:underline">Submit a request</Link>.</div>
    </div>
  );
}

function MyTickets() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/portal/tickets").then(r => setItems(asArray(r.data))).catch(() => setItems([])).finally(() => setLoading(false)); }, []);
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Help Desk</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">My Requests</h1>
        </div>
        <Link to="/support/new" data-testid="portal-new" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Plus size={15} /> Submit a request</Link>
      </div>

      {loading ? <div className="text-zinc-500 text-sm">Loading…</div> : items.length === 0 ? (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-10 text-center">
          <LifeBuoy size={28} className="text-zinc-600 mx-auto mb-3" />
          <div className="text-zinc-300">You haven't submitted any requests yet.</div>
          <Link to="/support/new" className="inline-flex items-center gap-1.5 mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Plus size={15} /> Submit your first request</Link>
        </div>
      ) : (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md divide-y divide-zinc-900">
          {items.map(t => (
            <Link key={t.id} to={`/support/${t.id}`} data-testid={`portal-ticket-${t.id}`} className="flex items-center gap-4 px-5 py-3.5 hover:bg-zinc-900/40">
              <Ticket size={16} className="text-zinc-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">{t.title}</div>
                <div className="text-[11px] text-zinc-500 font-mono mt-0.5">#{t.number} · {t.category} · {t.created_at?.slice(0, 10)}</div>
              </div>
              <SeverityBadge level={t.priority} />
              <StatusBadge status={t.status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function NewTicket() {
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", category: "General", priority: "medium", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Please enter a subject"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/tickets", form);
      toast.success(`Request #${data.number} submitted`);
      nav(`/support/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Link to="/support" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200"><ArrowLeft size={15} /> Back to my requests</Link>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Submit a request</h1>
      <form onSubmit={submit} className="bg-zinc-950 border border-zinc-900 rounded-md p-5 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Subject</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="nt-title" placeholder="Briefly, what do you need help with?" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Category</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="nt-category" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Priority</label>
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="nt-priority" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm capitalize">
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Details</label>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="nt-desc" rows={6} placeholder="Describe the issue, any error messages, and when it started." className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </div>
        <button type="submit" disabled={busy} data-testid="nt-submit" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded-md px-4 py-2">{busy ? "Submitting…" : "Submit request"} <Send size={14} /></button>
      </form>
    </div>
  );
}

function TicketThread() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const load = () => api.get(`/portal/tickets/${id}`).then(r => setT(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, [id]);
  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.post(`/portal/tickets/${id}/comments`, { body: reply }); setReply(""); load(); }
    catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };
  if (err) return <div className="space-y-4"><Link to="/support" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200"><ArrowLeft size={15} /> Back</Link><div className="text-sm text-zinc-500">Request not found.</div></div>;
  if (!t) return <div className="text-zinc-500 text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <Link to="/support" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200"><ArrowLeft size={15} /> Back to my requests</Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{t.title}</h1>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">#{t.number} · {t.category} · opened {t.created_at?.slice(0, 10)}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0"><SeverityBadge level={t.priority} /><StatusBadge status={t.status} /></div>
      </div>

      {t.description && <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 text-sm text-zinc-300 whitespace-pre-wrap">{t.description}</div>}

      <div className="space-y-3">
        {(t.comments || []).map(c => (
          <div key={c.id} className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium text-zinc-200">{c.actor_name || "Support"}</span>
              <span className="text-zinc-600 font-mono">{c.at?.slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="text-sm text-zinc-300 whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
        {(t.comments || []).length === 0 && <div className="text-sm text-zinc-500">No replies yet — our team will respond here.</div>}
      </div>

      <form onSubmit={send} className="bg-zinc-950 border border-zinc-900 rounded-md p-3 flex gap-2">
        <input value={reply} onChange={e => setReply(e.target.value)} data-testid="reply-body" placeholder="Add a reply…" className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        <button type="submit" disabled={busy} data-testid="reply-send" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-3 py-2"><Send size={14} /> Send</button>
      </form>
    </div>
  );
}

export default function SupportPortal() {
  return (
    <PortalLayout>
      <Routes>
        <Route index element={<MyTickets />} />
        <Route path="new" element={<NewTicket />} />
        <Route path="kb" element={<KbList />} />
        <Route path="kb/:id" element={<KbArticle />} />
        <Route path=":id" element={<TicketThread />} />
        <Route path="*" element={<Navigate to="/support" replace />} />
      </Routes>
    </PortalLayout>
  );
}
